import type { ReactNode } from "react";
import type {
  FieldError,
  FieldValues,
  Path,
  UseFormReturn,
} from "react-hook-form";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/** Pull a flat error message out of RHF's nested error tree by field name. */
function errorMessage(
  errors: Record<string, unknown>,
  name: string,
): string | undefined {
  const err = errors[name] as FieldError | undefined;
  return err?.message;
}

/**
 * Coerce a numeric input's string value to a number, mapping empty/invalid to
 * `undefined`. Used by {@link NumberField} instead of RHF's `valueAsNumber` so
 * an empty optional number resolves to `undefined` (accepted by the optional
 * Zod number schemas) rather than `NaN` (which silently blocks submission).
 */
function numberOrUndefined(v: string): number | undefined {
  if (v === "" || v == null) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

interface BaseFieldProps<TForm extends FieldValues> {
  form: UseFormReturn<TForm>;
  name: Path<TForm>;
  label: string;
  description?: string;
}

function FieldShell({
  label,
  htmlFor,
  description,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  description?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {description ? (
        <p className="text-xs text-muted-foreground">{description}</p>
      ) : null}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

/** Single-line text input bound to RHF. */
export function TextField<TForm extends FieldValues>({
  form,
  name,
  label,
  description,
  placeholder,
}: BaseFieldProps<TForm> & { placeholder?: string }) {
  const error = errorMessage(
    form.formState.errors as Record<string, unknown>,
    name,
  );
  return (
    <FieldShell
      label={label}
      htmlFor={name}
      description={description}
      error={error}
    >
      <Input id={name} placeholder={placeholder} {...form.register(name)} />
    </FieldShell>
  );
}

/** Multi-line textarea bound to RHF. */
export function TextAreaField<TForm extends FieldValues>({
  form,
  name,
  label,
  description,
  placeholder,
  rows = 4,
}: BaseFieldProps<TForm> & { placeholder?: string; rows?: number }) {
  const error = errorMessage(
    form.formState.errors as Record<string, unknown>,
    name,
  );
  return (
    <FieldShell
      label={label}
      htmlFor={name}
      description={description}
      error={error}
    >
      <Textarea
        id={name}
        rows={rows}
        placeholder={placeholder}
        {...form.register(name)}
      />
    </FieldShell>
  );
}

/** Numeric input bound to RHF; empty resolves to `undefined`, not `NaN`. */
export function NumberField<TForm extends FieldValues>({
  form,
  name,
  label,
  description,
  placeholder,
}: BaseFieldProps<TForm> & { placeholder?: string }) {
  const error = errorMessage(
    form.formState.errors as Record<string, unknown>,
    name,
  );
  return (
    <FieldShell
      label={label}
      htmlFor={name}
      description={description}
      error={error}
    >
      <Input
        id={name}
        type="number"
        placeholder={placeholder}
        {...form.register(name, { setValueAs: numberOrUndefined })}
      />
    </FieldShell>
  );
}

export interface SelectOption {
  value: string;
  label: string;
}

/**
 * Select bound to RHF via `watch`/`setValue`. `options` come from the shared
 * enum tuples. When `allowEmpty` is set, a sentinel clears the value (mapped to
 * `undefined`).
 */
export function SelectField<TForm extends FieldValues>({
  form,
  name,
  label,
  description,
  options,
  placeholder = "Select…",
  allowEmpty = false,
}: BaseFieldProps<TForm> & {
  options: readonly SelectOption[];
  placeholder?: string;
  allowEmpty?: boolean;
}) {
  const EMPTY = "__none__";
  const error = errorMessage(
    form.formState.errors as Record<string, unknown>,
    name,
  );
  const current = form.watch(name) as string | null | undefined;
  return (
    <FieldShell
      label={label}
      htmlFor={name}
      description={description}
      error={error}
    >
      <Select
        value={current ? String(current) : allowEmpty ? EMPTY : undefined}
        onValueChange={(value) =>
          form.setValue(
            name,
            (value === EMPTY ? null : value) as never,
            { shouldValidate: true, shouldDirty: true },
          )
        }
      >
        <SelectTrigger id={name}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {allowEmpty ? <SelectItem value={EMPTY}>— None —</SelectItem> : null}
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FieldShell>
  );
}

/**
 * Checkbox bound to RHF that stores an integer `0 | 1` (matching the D1 boolean
 * columns such as `is_start` / `is_terminal`). Toggles via watch/setValue.
 */
export function IntCheckboxField<TForm extends FieldValues>({
  form,
  name,
  label,
  description,
}: BaseFieldProps<TForm>) {
  const checked = Number(form.watch(name) ?? 0) === 1;
  return (
    <div className="space-y-1.5">
      <label className="flex items-center gap-2 text-sm font-medium">
        <input
          id={name}
          type="checkbox"
          className="h-4 w-4 rounded border-input"
          checked={checked}
          onChange={(e) =>
            form.setValue(name, (e.target.checked ? 1 : 0) as never, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
        />
        {label}
      </label>
      {description ? (
        <p className="text-xs text-muted-foreground">{description}</p>
      ) : null}
    </div>
  );
}

/** Build `SelectOption[]` from a shared enum tuple, humanizing labels. */
export function optionsFromEnum(values: readonly string[]): SelectOption[] {
  return values.map((value) => ({
    value,
    label: value
      .replace(/[_.]/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase()),
  }));
}
